export type Tipo = 'cadastro_usuario' | 'acesso_usuario' | 'dashboard_novo' | 'dashboard_alteracao' | 'outras_solicitacoes'

export interface Opcao { id: number; nome: string; detalhe?: string | null }

/** Um dashboard num grupo de um ambiente: a unidade em que o portal libera acesso. */
export interface Acesso {
  ambienteId: number; ambienteNome: string; grupoId: number; grupoNome: string; dashboardId: number; dashboardNome: string
}

/** O que a pessoa já acessa: ambientes, com os grupos e os dashboards de cada um. */
export interface ArvoreAmbiente {
  id: number
  nome: string
  grupos: { id: number; nome: string; dashboards: { id: number; nome: string }[] }[]
}

export const TIPOS: { id: Tipo; rotulo: string; ajuda: string }[] = [
  { id: 'cadastro_usuario', rotulo: 'Cadastro de usuário', ajuda: 'Pedir o cadastro de uma pessoa nova no portal.' },
  { id: 'acesso_usuario', rotulo: 'Acesso a dashboards', ajuda: 'Liberar dashboards ou equipes para quem já está cadastrado.' },
  { id: 'dashboard_novo', rotulo: 'Dashboard novo', ajuda: 'Pedir um dashboard que ainda não existe.' },
  { id: 'dashboard_alteracao', rotulo: 'Alteração de dashboard', ajuda: 'Corrigir ou mudar um dashboard existente.' },
  { id: 'outras_solicitacoes', rotulo: 'Outras solicitações', ajuda: 'Qualquer outro pedido ao time de BI que não se encaixe nos tipos acima.' }
]

export const rotuloTipo = (tipo: string) => TIPOS.find(t => t.id === tipo)?.rotulo || tipo

/** Estes dois tipos escolhem entre o que a pessoa já acessa: sem acesso nenhum, não dá para abrir. */
export const PRECISA_DE_ACESSO: Tipo[] = ['dashboard_novo', 'dashboard_alteracao']

export const STATUS: Record<string, { rotulo: string; tom: '' | 'ok' | 'alerta' | 'inativo' }> = {
  triagem: { rotulo: 'Em triagem', tom: '' },
  fila: { rotulo: 'Na fila', tom: '' },
  desenvolvimento: { rotulo: 'Em desenvolvimento', tom: '' },
  homologacao: { rotulo: 'Em homologação', tom: 'alerta' },
  aguardando_solicitante: { rotulo: 'Aguardando o solicitante', tom: 'alerta' },
  concluido: { rotulo: 'Concluído', tom: 'ok' },
  cancelado: { rotulo: 'Cancelado', tom: 'inativo' }
}

export const rotuloStatus = (s: string) => STATUS[s]?.rotulo || s
export const encerrado = (s: string) => s === 'concluido' || s === 'cancelado'

/** Valor do filtro de status que esconde o que já terminou (concluídas e canceladas): é como a lista de Solicitações abre. */
export const STATUS_EM_ABERTO = 'em_aberto'

export const PRIORIDADES = [
  { id: 'baixa', rotulo: 'Baixa' },
  { id: 'normal', rotulo: 'Normal' },
  { id: 'alta', rotulo: 'Alta' },
  { id: 'urgente', rotulo: 'Urgente' }
]
export const rotuloPrioridade = (p: string) => PRIORIDADES.find(x => x.id === p)?.rotulo || p

export const TIPOS_DE_ALTERACAO = [
  { id: '', rotulo: 'Não sei / outro' },
  { id: 'correcao', rotulo: 'Correção de erro' },
  { id: 'nova_metrica', rotulo: 'Nova métrica' },
  { id: 'ajuste_visual', rotulo: 'Ajuste visual' },
  { id: 'outro', rotulo: 'Outro' }
]

export interface Resumo {
  id: number; tipo: string; status: string; titulo: string; prioridade: string; prazo: string | null; bloqueado: boolean
  solicitanteId: number; solicitanteNome: string | null; responsavelId: number | null; responsavelNome: string | null
  criadoEm: string; atualizadoEm: string; esforco?: string | null; bloqueioMotivo?: string | null
  /** O ambiente (cliente) de dashboard novo e alteração, como estava quando o pedido foi aberto. */
  ambienteId?: number | null; ambienteNome?: string | null
}

export interface Detalhe extends Resumo {
  descricao: string; dados: any; esforco: string | null; bloqueioMotivo: string | null
  dashboardEntregueId: number | null; ehSolicitante: boolean; podeGerenciar: boolean
}

export interface Mensagem { id: number; autorId: number; autorNome: string | null; mensagem: string; interna: boolean; criadoEm: string }
export interface Historico { id: number; tipo: string; valorAnterior: string | null; valorNovo: string | null; usuarioId: number; usuarioNome: string | null; criadoEm: string }
/** Quem acompanha a solicitação de outra pessoa (compartilhada com ela). `podeTirar` vem do servidor. */
export interface Acompanhante { userId: number; nome: string | null; email: string | null; adicionadoPorId: number; adicionadoPorNome: string | null; criadoEm: string; podeTirar: boolean }
export interface Anexo { id: number; nome: string; tipoMime: string; tamanho: number; autorId: number; autorNome: string | null; criadoEm: string; abreNoNavegador: boolean }

// ---------------------------------------------------------------- datas

const dois = (n: number) => String(n).padStart(2, '0')

/** O banco grava em UTC; a tela mostra no horário de quem olha. */
export function dataHora(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return `${dois(d.getDate())}/${dois(d.getMonth() + 1)}/${d.getFullYear()} ${dois(d.getHours())}:${dois(d.getMinutes())}`
}

/** Prazo é um dia (AAAA-MM-DD), sem hora: não passa por fuso. */
export function dataDoPrazo(prazo: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(prazo || '')
  return m ? `${m[3]}/${m[2]}/${m[1]}` : ''
}

export function hojeISO(agora: Date = new Date()): string {
  return `${agora.getFullYear()}-${dois(agora.getMonth() + 1)}-${dois(agora.getDate())}`
}

/** Atrasada: prazo vencido e solicitação ainda aberta. */
export function atrasada(s: { prazo: string | null; status: string }, hoje: string = hojeISO()): boolean {
  return !!s.prazo && !encerrado(s.status) && s.prazo.slice(0, 10) < hoje
}

// ---------------------------------------------------------------- atendimento

/** Quem atende as solicitações (tem a claim gestao_solicitacoes no AuthManager). */
export interface Gestor { userId: number; nome: string; email: string | null }

type DoDetalhe = Pick<Detalhe, 'status' | 'ehSolicitante' | 'podeGerenciar'>

/** Em homologação, quem pediu (ou a gestão por ele) aceita ou recusa a entrega. */
export const podeAceitarOuRecusar = (d: DoDetalhe) => d.status === 'homologacao' && (d.ehSolicitante || d.podeGerenciar)

/** Encerrada: quem pediu ou a gestão pode reabrir. */
export const podeReabrir = (d: DoDetalhe) => encerrado(d.status) && (d.ehSolicitante || d.podeGerenciar)

/** Os status que a gestão pode escolher agora. Encerrada só volta por Reabrir. */
export const statusParaEscolher = (d: DoDetalhe): string[] =>
  !d.podeGerenciar || encerrado(d.status) ? [] : Object.keys(STATUS).filter(s => s !== d.status)

/** Vê só porque a solicitação foi compartilhada com a pessoa: acompanha, conversa e anexa, mas não decide nada. */
export const soAcompanha = (d: { ehSolicitante: boolean; podeGerenciar: boolean }) => !d.ehSolicitante && !d.podeGerenciar

/** Cancelar, recusar e reabrir pedem uma frase: o motivo. Só cancelar e recusar a exigem. */
export const motivoValido =(motivo: string) => motivo.trim().length >= 3

// ---------------------------------------------------------------- processar (aplicar o acesso)

/** Cada parte do plano exige a permissão da tela da Gestão que faz aquilo. */
export interface PermissoesProcessar { darAcesso: boolean; equipes: boolean; regras: boolean; criarLogin: boolean }

export interface PlanoVinculo {
  vinculoId: number; ambienteId: number; ambiente: string; grupoId?: number; grupo: string; dashboardId: number; dashboard: string
  regraId?: number | null; regra?: string | null; jaTem: boolean
}
export interface PlanoEquipe { id: number; nome: string; jaEsta: boolean }
export interface PlanoRegra {
  regraId: number; regra: string; atributo: string; valor: string; dashboardId: number | null; ambienteId: number | null
  dashboard: string | null; ambiente: string | null; jaTem: boolean
}
export interface RegraDisponivel { id: number; nome: string; coluna: string; atributos: string[] }

/** O cadastro do pedido e o que falta preencher para criar o login no AuthManager. */
export interface LoginParaCriar { configurado: boolean; nome: string; email: string; cpf: string | null; usernameSugerido: string; perfil: string }

export interface LoginCriado { userId: number; username: string; jaExistia: boolean; apareceNoPortal: boolean }

export interface Plano {
  solicitacaoId: number; tipo: string; origem: 'pedido' | 'espelho'; pessoaId: number | null; pessoaNome: string | null
  espelhoNome: string | null; aviso: string | null; vinculos: PlanoVinculo[]; equipes: PlanoEquipe[]; regras: PlanoRegra[]
  regrasDisponiveis: RegraDisponivel[]; abreVazio: string[]; permissoes: PermissoesProcessar; loginParaCriar: LoginParaCriar | null
}

export interface ResultadoProcessar {
  acessosCriados: number; acessosQueJaTinha: number; equipesAdicionadas: number; equipesQueJaEstava: number; regrasCriadas: number
  regrasQueJaTinha: number; abreVazio: string[]; solicitacao: Detalhe
}

/** Só pedidos de acesso e de cadastro são processados, e só pela gestão, enquanto não estiverem encerrados. */
export const podeProcessar = (d: { tipo: string; status: string; podeGerenciar: boolean }) =>
  d.podeGerenciar && (d.tipo === 'acesso_usuario' || d.tipo === 'cadastro_usuario') && !encerrado(d.status)

/** O que a gestão preenche para criar o login: usuário, empresa e se é interna ou cliente. O servidor confere de novo. */
export interface FormLogin { username: string; empresa: string; tipo: '' | 'interno' | 'cliente' }

export function faltaNoLogin(f: FormLogin): string | null {
  if (!/^[a-zA-Z0-9_.-]{3,40}$/.test(f.username.trim())) return 'O usuário precisa ter de 3 a 40 letras, números, ponto, traço ou sublinhado.'
  const empresa = f.empresa.trim()
  if (empresa.length < 2 || empresa.length > 100) return 'Informe a empresa da pessoa.'
  if (!f.tipo) return 'Diga se a pessoa é interna ou cliente.'
  return null
}

export const corpoDoLogin = (f: FormLogin) => ({ username: f.username.trim(), empresa: f.empresa.trim(), interno: f.tipo === 'interno' })

/** O alcance de um valor de regra, em palavras. */
export const alcanceDaRegra = (r: { dashboard: string | null; ambiente: string | null }) =>
  r.dashboard && r.ambiente ? `${r.dashboard} · ${r.ambiente}` : 'Todos os dashboards da regra'

export const chaveDaRegra = (r: { regraId: number; atributo: string; valor: string; dashboardId: number | null; ambienteId: number | null }) =>
  [r.regraId, r.atributo.trim().toLowerCase(), r.valor.trim().toLowerCase(), r.dashboardId ?? '', r.ambienteId ?? ''].join('|')

/** O que o plano vai de fato mudar (o que a pessoa já tem não conta) e se ele está vazio. */
export function resumoDoPlano(v: PlanoVinculo[], e: PlanoEquipe[], r: PlanoRegra[]) {
  const novosAcessos = v.filter(x => !x.jaTem).length
  const novasEquipes = e.filter(x => !x.jaEsta).length
  const novasRegras = r.filter(x => !x.jaTem).length
  return {
    novosAcessos, jaTemAcessos: v.length - novosAcessos, novasEquipes, jaEstaEquipes: e.length - novasEquipes, novasRegras,
    vazio: v.length + e.length + r.length === 0
  }
}

/** O que vai para o servidor: a pessoa e os ids que sobraram no plano. O servidor confere de novo a pessoa e as permissões. */
export function corpoDoProcessar(pessoaId: number, v: PlanoVinculo[], e: PlanoEquipe[], r: PlanoRegra[]) {
  return {
    pessoaId,
    vinculos: v.map(x => x.vinculoId),
    equipes: e.map(x => x.id),
    regras: r.map(x => ({ regraId: x.regraId, atributo: x.atributo.trim(), valor: x.valor.trim(), dashboardId: x.dashboardId, ambienteId: x.ambienteId }))
  }
}

// ---------------------------------------------------------------- lista

export const semAcento = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/** Aplica os filtros da tela. Fica fora do componente para o teste cobrir. */
export function filtrar(
  lista: Resumo[],
  f: { busca: string; tipo: string; status: string; prioridade: string; soAtrasadas: boolean; responsavel?: string; ambienteId?: string; soBloqueadas?: boolean; nasConversas?: ReadonlySet<number> },
  hoje?: string
): Resumo[] {
  const alvo = semAcento(f.busca.trim())
  return lista.filter(s => {
    if (f.tipo && s.tipo !== f.tipo) return false
    if (f.status === STATUS_EM_ABERTO ? encerrado(s.status) : f.status && s.status !== f.status) return false
    if (f.prioridade && s.prioridade !== f.prioridade) return false
    if (f.soAtrasadas && !atrasada(s, hoje)) return false
    if (f.soBloqueadas && !s.bloqueado) return false
    if (f.ambienteId && String(s.ambienteId) !== f.ambienteId) return false
    if (f.responsavel === 'nenhum' && s.responsavelId != null) return false
    if (f.responsavel && f.responsavel !== 'nenhum' && String(s.responsavelId) !== f.responsavel) return false
    if (alvo) {
      const palavras = semAcento(`${s.id} ${s.titulo} ${s.solicitanteNome || ''} ${s.responsavelNome || ''} ${s.ambienteNome || ''}`)
      // Acha no número, título ou pessoas; ou, se o servidor achou o texto na conversa, também.
      if (!palavras.includes(alvo.replace(/^#/, '')) && !f.nasConversas?.has(s.id)) return false
    }
    return true
  })
}

// ---------------------------------------------------------------- kanban e triagem

/** As colunas do kanban, na ordem do ciclo de vida. */
export const COLUNAS = ['triagem', 'fila', 'desenvolvimento', 'homologacao', 'aguardando_solicitante', 'concluido', 'cancelado']

const PESO_DA_PRIORIDADE: Record<string, number> = { urgente: 0, alta: 1, normal: 2, baixa: 3 }

/** Ordem dentro da coluna: prioridade, depois o prazo mais próximo (sem prazo por último), depois a mais antiga primeiro. */
export function ordemDoKanban(a: Resumo, b: Resumo): number {
  const pa = PESO_DA_PRIORIDADE[a.prioridade] ?? 9, pb = PESO_DA_PRIORIDADE[b.prioridade] ?? 9
  if (pa !== pb) return pa - pb
  const prazoA = a.prazo ? a.prazo.slice(0, 10) : '9999-99-99', prazoB = b.prazo ? b.prazo.slice(0, 10) : '9999-99-99'
  if (prazoA !== prazoB) return prazoA < prazoB ? -1 : 1
  return a.criadoEm.localeCompare(b.criadoEm) || a.id - b.id
}

/** Agrupa por status, já na ordem de cada coluna. Status que o kanban não conhece cai na triagem. */
export function porColuna(lista: Resumo[]): Record<string, Resumo[]> {
  const mapa: Record<string, Resumo[]> = Object.fromEntries(COLUNAS.map(c => [c, [] as Resumo[]]))
  lista.forEach(s => mapa[COLUNAS.includes(s.status) ? s.status : 'triagem'].push(s))
  COLUNAS.forEach(c => mapa[c].sort(ordemDoKanban))
  return mapa
}

export type Movimento = 'igual' | 'encerrada' | 'cancelar' | 'ok'

/** O que acontece ao soltar um cartão numa coluna. Encerrada não sai arrastando (reabre no detalhe); cancelar pede o motivo. */
export function movimento(de: string, para: string): Movimento {
  if (de === para) return 'igual'
  if (encerrado(de)) return 'encerrada'
  return para === 'cancelado' ? 'cancelar' : 'ok'
}

export const ESFORCOS = [{ id: '', rotulo: 'Sem estimativa' }, { id: 'P', rotulo: 'P (pequeno)' }, { id: 'M', rotulo: 'M (médio)' }, { id: 'G', rotulo: 'G (grande)' }]

export interface FormTriagem { prioridade: string; prazo: string; esforco: string }

export const triagemDe = (d: { prioridade: string; prazo: string | null; esforco: string | null }): FormTriagem =>
  ({ prioridade: d.prioridade, prazo: d.prazo ? d.prazo.slice(0, 10) : '', esforco: d.esforco || '' })

export const corpoDaTriagem = (f: FormTriagem) => ({ prioridade: f.prioridade, prazo: f.prazo || null, esforco: f.esforco || null })

// ---------------------------------------------------------------- histórico

export function textoDoHistorico(h: Historico): string {
  const de = h.valorAnterior, para = h.valorNovo
  switch (h.tipo) {
    case 'criacao': return `Abriu a solicitação (${rotuloTipo(para || '')})`
    case 'status': return `Mudou o status: ${rotuloStatus(de || '')} → ${rotuloStatus(para || '')}`
    case 'responsavel': return para ? `Passou a atender: ${para}` : 'Tirou o responsável'
    case 'prioridade': return `Prioridade: ${rotuloPrioridade(de || '')} → ${rotuloPrioridade(para || '')}`
    case 'prazo': return para ? `Prazo: ${dataDoPrazo(para)}` : 'Removeu o prazo'
    case 'esforco': return `Esforço: ${para || 'sem estimativa'}`
    case 'bloqueio': return para ? `Bloqueou: ${para}` : 'Tirou o bloqueio'
    case 'reabertura': return 'Reabriu a solicitação'
    case 'acompanhante': return para ? `Compartilhou com ${para}` : `Tirou ${de || 'alguém'} de quem acompanha`
    case 'dados': return 'Ajustou o que foi pedido'
    default: return h.tipo
  }
}

// ---------------------------------------------------------------- cadastro: CPF e e-mail

export const soDigitos = (t: string | null | undefined) => (t || '').replace(/\D/g, '')

export function dominioDoEmail(email: string): string | null {
  const i = email.lastIndexOf('@')
  return i < 0 || i === email.length - 1 ? null : email.slice(i + 1).trim().toLowerCase()
}

/** O AuthManager exige CPF para os e-mails destes domínios (o domínio inteiro, depois do @). */
export function cpfObrigatorio(email: string, dominiosComCpf: string[]): boolean {
  const d = dominioDoEmail(email.trim())
  return !!d && dominiosComCpf.some(x => x.trim().replace(/^@/, '').toLowerCase() === d)
}

/** Os dígitos verificadores do CPF. O servidor confere de novo. */
export function cpfValido(valor: string): boolean {
  const c = soDigitos(valor)
  if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false
  const digito = (n: number) => {
    let soma = 0
    for (let i = 0; i < n; i++) soma += Number(c[i]) * (n + 1 - i)
    const resto = (soma * 10) % 11
    return resto === 10 ? 0 : resto
  }
  return digito(9) === Number(c[9]) && digito(10) === Number(c[10])
}

export function mascaraCpf(valor: string): string {
  const d = soDigitos(valor).slice(0, 11)
  return d.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

/** O que o servidor respondeu sobre o e-mail e o CPF digitados. */
export interface ConferenciaCadastro { emailJaExiste: boolean; cpfObrigatorio: boolean; cpfInvalido: boolean; cpfJaExiste: boolean }

// ---------------------------------------------------------------- formulário de abertura

/** Como orientar o acesso: uma das três, e só o campo dela aparece. */
export type ViaAcesso = '' | 'dashboards' | 'equipes' | 'espelho'

export const VIAS: { id: Exclude<ViaAcesso, ''>; rotulo: string }[] = [
  { id: 'dashboards', rotulo: 'Dashboards' },
  { id: 'equipes', rotulo: 'Equipes' },
  { id: 'espelho', rotulo: 'Usuário espelho' }
]

export interface FormNova {
  tipo: Tipo | ''
  titulo: string; descricao: string; prioridade: string; prazo: string
  nome: string; email: string; cpf: string
  pessoa: Opcao | null
  via: ViaAcesso
  acessos: Acesso[]; equipes: Opcao[]; espelho: Opcao | null
  cliente: Opcao | null
  dashboard: Opcao | null; tipoAlteracao: string
  /** Quem mais acompanha o pedido desde o início. */
  acompanhantes: Opcao[]
}

export const FORM_VAZIO: FormNova = {
  tipo: '', titulo: '', descricao: '', prioridade: 'normal', prazo: '',
  nome: '', email: '', cpf: '', pessoa: null, via: '', acessos: [], equipes: [], espelho: null,
  cliente: null,
  dashboard: null, tipoAlteracao: '', acompanhantes: []
}

const ref = (o: Opcao) => ({ id: o.id, nome: o.nome })
const refs = (l: Opcao[]) => l.map(ref)
const texto = (v: string) => v.trim()

/** Só o caminho escolhido vai no pedido: o que sobrou preenchido nos outros dois fica de fora. */
function caminho(f: FormNova) {
  return {
    acessos: f.via === 'dashboards' ? f.acessos : [],
    equipes: f.via === 'equipes' ? refs(f.equipes) : [],
    espelho: f.via === 'espelho' && f.espelho ? ref(f.espelho) : null
  }
}

/** O que falta no caminho escolhido. No cadastro o caminho é opcional; no pedido de acesso, obrigatório. */
function faltaNoCaminho(f: FormNova, obrigatorio: boolean): string | null {
  if (!f.via) return obrigatorio ? 'Escolha como orientar o acesso: dashboards, equipes ou usuário espelho.' : null
  if (f.via === 'dashboards') return f.acessos.length > 0 ? null : 'Escolha pelo menos um dashboard.'
  if (f.via === 'equipes') return f.equipes.length > 0 ? null : 'Escolha pelo menos uma equipe.'
  if (!f.espelho) return 'Escolha a pessoa de referência.'
  if (f.pessoa && f.espelho.id === f.pessoa.id) return 'A pessoa de referência precisa ser diferente de quem vai receber o acesso.'
  return null
}

/** O que vai em "dados" de cada tipo. O servidor confere de novo e descarta o que não é do tipo. */
export function montarDados(f: FormNova): Record<string, unknown> {
  switch (f.tipo) {
    case 'cadastro_usuario':
      return { nome: texto(f.nome), email: texto(f.email), cpf: soDigitos(f.cpf), ...caminho(f) }
    case 'acesso_usuario':
      return { pessoa: f.pessoa ? ref(f.pessoa) : null, ...caminho(f) }
    case 'dashboard_novo':
      return { cliente: f.cliente ? ref(f.cliente) : null }
    case 'dashboard_alteracao':
      return { cliente: f.cliente ? ref(f.cliente) : null, dashboard: f.dashboard ? ref(f.dashboard) : null, tipoAlteracao: f.tipoAlteracao }
    default:
      return {}
  }
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

/** O que o formulário sabe além dos campos: os domínios que exigem CPF e a conferência do servidor. */
export interface ContextoForm {
  hoje?: string
  dominiosComCpf?: string[]
  conferencia?: ConferenciaCadastro | null
}

/** A primeira coisa que falta no formulário, ou null. O servidor tem a palavra final. */
export function faltaNoForm(f: FormNova, ctx: ContextoForm = {}): string | null {
  const hoje = ctx.hoje || hojeISO()
  if (!f.tipo) return 'Escolha o tipo de solicitação.'
  if (texto(f.titulo).length < 3) return 'Dê um título (pelo menos 3 letras).'
  if (texto(f.descricao).length < 3) return 'Descreva o que você precisa.'
  if (f.prazo && f.prazo < hoje) return 'O prazo desejado não pode ser uma data que já passou.'
  switch (f.tipo) {
    case 'cadastro_usuario': {
      if (texto(f.nome).length < 3) return 'Informe o nome da pessoa.'
      if (!EMAIL.test(texto(f.email))) return 'Informe um e-mail válido.'
      if (ctx.conferencia?.emailJaExiste) return 'Já existe uma pessoa com esse e-mail no portal.'
      const cpf = soDigitos(f.cpf)
      if (cpf.length === 0 && cpfObrigatorio(f.email, ctx.dominiosComCpf || [])) return 'Para este e-mail o cadastro exige o CPF da pessoa.'
      if (cpf.length > 0 && !cpfValido(cpf)) return 'Informe um CPF válido.'
      if (cpf.length > 0 && ctx.conferencia?.cpfJaExiste) return 'Esse CPF já consta em outra pessoa do portal.'
      return faltaNoCaminho(f, false)
    }
    case 'acesso_usuario':
      if (!f.pessoa) return 'Escolha a pessoa.'
      return faltaNoCaminho(f, true)
    case 'dashboard_novo':
      return f.cliente ? null : 'Escolha o cliente.'
    case 'dashboard_alteracao':
      if (!f.cliente) return 'Escolha o ambiente.'
      if (!f.dashboard) return 'Escolha o dashboard.'
      return null
    case 'outras_solicitacoes':
      // Só título e descrição, já conferidos acima.
      return null
  }
}

export function corpoDaAbertura(f: FormNova) {
  return {
    tipo: f.tipo, titulo: texto(f.titulo), descricao: texto(f.descricao), prioridade: f.prioridade,
    prazo: f.prazo || null, dados: montarDados(f), acompanhantes: f.acompanhantes.map(a => a.id)
  }
}

// ---------------------------------------------------------------- escolher dashboards (ambiente primeiro)

export const chaveDoAcesso = (a: { ambienteId: number; grupoId: number; dashboardId: number }) => `${a.ambienteId}|${a.grupoId}|${a.dashboardId}`

/** Todos os dashboards de um ambiente, no formato do pedido. */
export function acessosDoAmbiente(amb: ArvoreAmbiente): Acesso[] {
  return amb.grupos.flatMap(g => g.dashboards.map(d => ({
    ambienteId: amb.id, ambienteNome: amb.nome, grupoId: g.id, grupoNome: g.nome, dashboardId: d.id, dashboardNome: d.nome
  })))
}

/** Marca todos os dashboards do ambiente; se já estavam todos marcados, desmarca. */
export function alternarTodos(atuais: Acesso[], amb: ArvoreAmbiente): Acesso[] {
  const doAmbiente = acessosDoAmbiente(amb)
  const chaves = new Set(atuais.map(chaveDoAcesso))
  const todos = doAmbiente.length > 0 && doAmbiente.every(a => chaves.has(chaveDoAcesso(a)))
  if (todos) {
    const sai = new Set(doAmbiente.map(chaveDoAcesso))
    return atuais.filter(a => !sai.has(chaveDoAcesso(a)))
  }
  return [...atuais, ...doAmbiente.filter(a => !chaves.has(chaveDoAcesso(a)))]
}

/**
 * Os dashboards de um ambiente sem repetir (o mesmo dashboard pode estar em dois grupos), com o grupo se o nome repetir.
 * Com `comGrupo`, todos levam o(s) grupo(s) no detalhe, para a pessoa saber de que área é o dashboard.
 */
export function dashboardsDoAmbiente(amb: ArvoreAmbiente | undefined, comGrupo = false): Opcao[] {
  if (!amb) return []
  const porId = new Map<number, { nome: string; grupos: string[] }>()
  amb.grupos.forEach(g => g.dashboards.forEach(d => {
    const item = porId.get(d.id) || { nome: d.nome, grupos: [] }
    item.grupos.push(g.nome)
    porId.set(d.id, item)
  }))
  const nomes = Array.from(porId.values()).map(x => x.nome)
  return Array.from(porId.entries())
    .map(([id, x]) => ({ id, nome: x.nome, detalhe: comGrupo || nomes.filter(n => n === x.nome).length > 1 ? x.grupos.join(', ') : null }))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
}

/** Os ambientes que aparecem nos pedidos, sem repetir e em ordem alfabética: as opções do filtro de ambiente. */
export function ambientesDosPedidos(lista: Resumo[]): Opcao[] {
  const mapa = new Map<number, string>()
  lista.forEach(s => { if (s.ambienteId != null && s.ambienteNome) mapa.set(s.ambienteId, s.ambienteNome) })
  return Array.from(mapa.entries()).map(([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
}

/** Procurar numa lista que já está na tela: pelo nome ou pelo detalhe (o grupo), sem ligar para acento nem maiúscula. Sem termo, devolve tudo. */
export function filtrarOpcoes(opcoes: Opcao[], termo: string): Opcao[] {
  const alvo = semAcento(termo.trim())
  if (!alvo) return opcoes
  return opcoes.filter(o => semAcento(`${o.nome} ${o.detalhe || ''}`).includes(alvo))
}

/** O nome na lista de escolha: "Atendimento" ou, se repetir no ambiente, "Atendimento · Comercial". */
export const rotuloDoDashboard = (o: Opcao) => (o.detalhe ? `${o.nome} · ${o.detalhe}` : o.nome)

/** Os acessos agrupados por ambiente, para mostrar o resumo do pedido. */
export function porAmbiente(acessos: Acesso[]): { ambienteId: number; ambienteNome: string; itens: Acesso[] }[] {
  const mapa = new Map<number, { ambienteId: number; ambienteNome: string; itens: Acesso[] }>()
  acessos.forEach(a => {
    const g = mapa.get(a.ambienteId) || { ambienteId: a.ambienteId, ambienteNome: a.ambienteNome, itens: [] }
    g.itens.push(a)
    mapa.set(a.ambienteId, g)
  })
  return Array.from(mapa.values())
}

// ---------------------------------------------------------------- o pedido, para ler

export interface LinhaDoPedido { rotulo: string; valor: string }

const nomes = (l: any): string => (Array.isArray(l) ? l.map((i: any) => i?.nome).filter(Boolean).join(', ') : '')

const acessosEmTexto = (l: any): string =>
  Array.isArray(l) ? l.map((a: any) => [a?.ambienteNome, a?.grupoNome, a?.dashboardNome].filter(Boolean).join(' › ')).filter(Boolean).join('; ') : ''

/** As linhas "campo: valor" que a tela mostra, só as preenchidas. Entende também os pedidos feitos antes das mudanças de formulário. */
export function linhasDoPedido(tipo: string, dados: any): LinhaDoPedido[] {
  const d = dados || {}
  const linhas: LinhaDoPedido[] = []
  const add = (rotulo: string, valor?: string | null) => { if (valor) linhas.push({ rotulo, valor }) }
  const dashboards = acessosEmTexto(d.acessos) || nomes(d.dashboards)
  switch (tipo) {
    case 'cadastro_usuario':
      add('Nome', d.nome); add('E-mail', d.email); add('CPF', d.cpf ? mascaraCpf(d.cpf) : d.cpfOculto ? 'Informado (não aparece para você)' : ''); add('Dashboards', dashboards)
      add('Equipes', nomes(d.equipes)); add('Mesmo acesso de', d.espelho?.nome)
      break
    case 'acesso_usuario':
      add('Pessoa', d.pessoa?.nome); add('Dashboards', dashboards); add('Equipes', nomes(d.equipes)); add('Mesmo acesso de', d.espelho?.nome)
      break
    case 'dashboard_novo':
      add('Cliente', d.cliente?.nome); add('Nome proposto', d.nomeProposto); add('Objetivo', d.objetivo); add('KPIs', d.kpis)
      add('Fonte dos dados', d.fonteDados); add('Público', d.publico); add('Frequência', d.frequencia)
      break
    case 'dashboard_alteracao':
      add('Ambiente', d.cliente?.nome); add('Dashboard', d.dashboard?.nome)
      add('Tipo da alteração', TIPOS_DE_ALTERACAO.find(t => t.id && t.id === d.tipoAlteracao)?.rotulo)
      add('Página ou visual', d.pagina); add('Link', d.link)
      break
  }
  return linhas
}

// ---------------------------------------------------------------- anexos

export const MAX_ANEXOS = 5
export const MAX_BYTES = 4 * 1024 * 1024
export const EXTENSOES = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'pdf', 'xlsx', 'xls', 'csv', 'docx', 'pptx', 'txt']

export function conferirArquivo(nome: string, tamanho: number): string | null {
  const ext = (nome.split('.').pop() || '').toLowerCase()
  if (!nome.includes('.') || !EXTENSOES.includes(ext)) return 'Esse tipo de arquivo não é aceito. Use imagem, PDF, planilha, Word, PowerPoint ou texto.'
  if (tamanho === 0) return 'O arquivo está vazio.'
  if (tamanho > MAX_BYTES) return 'O arquivo passa de 4 MB.'
  return null
}

export function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`
}

/** Leva o item para o lugar `destino` (0 é o primeiro), sem mexer na lista original. Id que não está na lista ou lugar fora dela não muda nada. */
export function reposicionar<T extends { id: number }>(lista: T[], id: number, destino: number): T[] {
  const de = lista.findIndex(x => x.id === id)
  if (de < 0) return lista
  const para = Math.max(0, Math.min(lista.length - 1, destino))
  if (para === de) return lista
  const nova = lista.slice()
  const [item] = nova.splice(de, 1)
  nova.splice(para, 0, item)
  return nova
}

/** Mudou a ordem dos ids em relação ao que está salvo? */
export const ordemMudou = (a: { id: number }[], b: { id: number }[]): boolean =>
  a.length !== b.length || a.some((x, i) => x.id !== b[i].id)
